import {
  CreateAssociationFields,
  CRMFilterOverride,
  CrmObjectDependencies,
  CustomObjectAssociationsComponent,
  CustomObjectToCompanyAssociationsPanelComponent,
  CustomObjectToContactAssociationsPanelComponent,
  FormCustomInput,
  FormDefaultInputValue,
  ProfileCard,
  StandardIds,
  SystemFieldIds,
  TableCustomCell,
  MultiRowAction,
  SingleRowAction,
} from '@galaxy/crm/static';
import { Config, Services } from './interface';
import { combineLatest, Observable, of } from 'rxjs';
import { CrmObjectInterface } from '@vendasta/crm';
import {
  CrmUserColumnCellComponent,
  CrmUserFilterChipComponent,
  CrmUserFilterInputComponent,
  CrmUserInputComponent,
} from '@galaxy/crm/components/user';
import { map } from 'rxjs/operators';
import {
  AddToListMultiRowAction,
  AddToListSelectAllAction,
  AddToListSingleRowAction,
} from '@galaxy/crm/integrations/dynamic-lists-actions';

export class CustomObjectDependencies {
  constructor(
    private readonly services: Services,
    private readonly config: Config,
  ) {}

  build(): CrmObjectDependencies {
    return {
      baseColumnIds: this.baseColumnIds(),
      getDisplayName: (crmObject: CrmObjectInterface) => {
        return this.services.crmObjectDisplayService.customObjectDisplayName(crmObject);
      },
      createAssociationFields: this.createAssociationFields(),
      profileCards$: this.profileCards$(),
      filterInputOverrides$: this.filterInputOverrides$(),
      tableCustomCells: this.tableCustomCells(),
      formCustomInputs: this.formCustomInput(),
      formDefaultOnCreateInputValues$: this.formDefaultOnCreateInputValues$(),
      multiRowTableActions$: this.multiRowTableActions$(),
      singleRowTableActions$: this.singleRowTableActions$(),
      selectAllTableActions$: this.selectAllTableActions$(),
    };
  }

  static build(services: Services, config: Config): CrmObjectDependencies {
    const instance = new CustomObjectDependencies(services, config);
    return instance.build();
  }

  private baseColumnIds(): string[] {
    return [StandardIds.CustomObjectName];
  }

  private profileCards$(): Observable<ProfileCard[]> {
    const profileCards = [
      { component: CustomObjectToContactAssociationsPanelComponent },
      { component: CustomObjectToCompanyAssociationsPanelComponent },
      { component: CustomObjectAssociationsComponent },
    ] as ProfileCard[];
    return of(profileCards);
  }

  private createAssociationFields(): CreateAssociationFields {
    return {
      objectTypes: ['Contact', 'Company', 'Opportunity', 'CustomObject'],
      required: false,
    };
  }

  private multiRowTableActions$(): Observable<MultiRowAction[]> {
    return combineLatest([this.config.canAddToList$, this.services.partnerService.getPartnerId()]).pipe(
      map(([canAddToList, partnerId]) => {
        const actions: MultiRowAction[] = [];
        if (canAddToList) {
          actions.push(
            AddToListMultiRowAction(
              partnerId,
              this.services.listActionsService,
              'CustomObject',
              this.services.translationService.instant('CRM.ADD_TO_STATIC_LIST'),
            ),
          );
        }
        return actions;
      }),
    );
  }

  private singleRowTableActions$(): Observable<SingleRowAction[]> {
    return combineLatest([this.config.canAddToList$, this.services.partnerService.getPartnerId()]).pipe(
      map(([canAddToList, partnerId]) => {
        const actions: SingleRowAction[] = [];
        if (canAddToList) {
          actions.push(
            AddToListSingleRowAction(
              partnerId,
              this.services.listActionsService,
              'CustomObject',
              this.services.translationService.instant('CRM.ADD_TO_STATIC_LIST'),
            ),
          );
        }
        return actions;
      }),
    );
  }

  private filterInputOverrides$(): Observable<CRMFilterOverride[]> {
    return of([
      {
        title: this.services.translationService.instant('CRM.TABLE.CUSTOM_COLUMNS.OWNER'),
        fieldId: SystemFieldIds.CustomObjectOwnerID,
        filterInput: CrmUserFilterInputComponent,
        filterChip: CrmUserFilterChipComponent,
      },
    ] as CRMFilterOverride[]);
  }

  private tableCustomCells(): TableCustomCell[] {
    return [
      {
        fieldIds: [SystemFieldIds.CustomObjectOwnerID],
        columnDefinition: {
          id: SystemFieldIds.CustomObjectOwnerID,
        },
        customCellComponent: CrmUserColumnCellComponent,
      },
    ] as TableCustomCell[];
  }

  private formCustomInput(): FormCustomInput[] {
    return [
      {
        fieldId: SystemFieldIds.CustomObjectOwnerID,
        component: CrmUserInputComponent,
      },
    ] as FormCustomInput[];
  }

  private formDefaultOnCreateInputValues$(): Observable<FormDefaultInputValue[]> {
    return this.services.appConfigService.config$.pipe(
      map((appConfig) => {
        return [{ fieldId: SystemFieldIds.CustomObjectOwnerID, value: appConfig.unifiedUserId }];
      }),
    );
  }

  private selectAllTableActions$(): Observable<MultiRowAction[]> {
    return combineLatest([this.config.canAddToList$, this.services.partnerService.getPartnerId()]).pipe(
      map(([canAddToList, partnerId]) => {
        const actions: MultiRowAction[] = [];
        if (canAddToList) {
          actions.push(
            AddToListSelectAllAction(
              partnerId,
              this.services.listActionsService,
              'CustomObject',
              this.services.translationService.instant('CRM.ADD_TO_STATIC_LIST'),
            ),
          );
        }
        return actions;
      }),
    );
  }
}
